import { useCallback, useEffect, useState } from "react";
import { fetchGroupDetail } from "../groupsApi";

/**
 * Inner async loader; kept outside the hook so it stays under 25 lines.
 * @param {Object} args - { groupId, signal, setDetail, setError, setLoading }
 */
async function loadDetail({ groupId, signal, setDetail, setError, setLoading }) {
    setLoading(true);
    setError(null);
    try {
        const data = await fetchGroupDetail(groupId, { signal });
        if (!signal.aborted) setDetail(data || null);
    } catch (err) {
        if (signal.aborted) return;
        setError(err?.response?.data || "Impossible de charger le groupe");
        setDetail(null);
    } finally {
        if (!signal.aborted) setLoading(false);
    }
}

/**
 * Custom hook fetching the full detail of a group.
 * Resets to null whenever groupId becomes falsy (drawer closed).
 * @param {number|string|null} groupId
 * @returns {{ detail: Object|null, loading: boolean, error: any, refresh: Function }}
 */
function useGroupDetail(groupId) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [tick, setTick] = useState(0);

    useEffect(() => {
        if (!groupId) {
            setDetail(null);
            setError(null);
            setLoading(false);
            return undefined;
        }
        const controller = new AbortController();
        loadDetail({ groupId, signal: controller.signal, setDetail, setError, setLoading });
        return () => controller.abort();
    }, [groupId, tick]);

    const refresh = useCallback(() => setTick((t) => t + 1), []);

    return { detail, loading, error, refresh };
}

export default useGroupDetail;
